import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import CustomerRequestLocationService from "../../../../infrastructure/api/customerRequestLocation";

const optionId = (item) => {
  const id = item?.id ?? item?.Id ?? item?.value ?? item?.Value ?? "";
  if (id === "" || id == null) return "";
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : String(id);
};

const optionLabel = (item, nameKeys = []) => {
  for (const key of nameKeys) {
    const value = item?.[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return item?.label ?? item?.Label ?? item?.title ?? item?.Title ?? "—";
};

const normalizeText = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/**
 * Province/city filters for supervisor list pages.
 */
export const useSupervisorLocationFilters = () => {
  const [provinceFilter, setProvinceFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    let cancelled = false;
    CustomerRequestLocationService.loadCustomerRequestLocationLists()
      .then(({ provinces: provinceList, cities: cityList }) => {
        if (cancelled) return;
        setProvinces(Array.isArray(provinceList) ? provinceList : []);
        setCities(Array.isArray(cityList) ? cityList : []);
      })
      .catch(() => {
        if (cancelled) return;
        setProvinces([]);
        setCities([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredCities = useMemo(() => {
    if (!provinceFilter) return [];
    const pid = Number(provinceFilter);
    return cities.filter((c) => {
      const cpid = Number(c?.provinceId ?? c?.ProvinceId ?? 0);
      return cpid === pid;
    });
  }, [cities, provinceFilter]);

  const selectedProvinceLabel = useMemo(() => {
    if (!provinceFilter) return "";
    const found = provinces.find((p) => String(optionId(p)) === String(provinceFilter));
    return found
      ? optionLabel(found, ["provinceName", "ProvinceName", "label"])
      : "";
  }, [provinceFilter, provinces]);

  const selectedCityLabel = useMemo(() => {
    if (!cityFilter) return "";
    const found = cities.find((c) => String(optionId(c)) === String(cityFilter));
    return found ? optionLabel(found, ["cityName", "CityName", "label"]) : "";
  }, [cityFilter, cities]);

  const matchesLocation = (row) => {
    if (!provinceFilter && !cityFilter) return true;

    const rowProvinceId = Number(
      row?.provinceId ?? row?.ProvinceId ?? row?._meta?.provinceId ?? 0
    );
    const rowCityId = Number(
      row?.cityId ?? row?.CityId ?? row?._meta?.cityId ?? 0
    );
    const rowProvinceName = normalizeText(
      row?.provinceName ??
        row?.ProvinceName ??
        row?.province ??
        row?._meta?.province ??
        ""
    );
    const rowCityName = normalizeText(
      row?.cityName ?? row?.CityName ?? row?.city ?? row?._meta?.city ?? ""
    );

    if (provinceFilter) {
      const wantId = Number(provinceFilter);
      const wantName = normalizeText(selectedProvinceLabel);
      const idMatch = wantId > 0 && rowProvinceId === wantId;
      const nameMatch =
        wantName && rowProvinceName && rowProvinceName === wantName;
      if (!idMatch && !nameMatch) return false;
    }

    if (cityFilter) {
      const wantId = Number(cityFilter);
      const wantName = normalizeText(selectedCityLabel);
      const idMatch = wantId > 0 && rowCityId === wantId;
      const nameMatch = wantName && rowCityName && rowCityName === wantName;
      if (!idMatch && !nameMatch) return false;
    }

    return true;
  };

  const onProvinceChange = (value) => {
    setProvinceFilter(value);
    setCityFilter("");
  };

  return {
    provinceFilter,
    cityFilter,
    provinces,
    filteredCities,
    setProvinceFilter: onProvinceChange,
    setCityFilter,
    matchesLocation,
  };
};

export const SupervisorLocationFilterBar = ({
  provinceFilter,
  cityFilter,
  provinces,
  filteredCities,
  onProvinceChange,
  onCityChange,
}) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={1.5}
    sx={{ mb: 1.5, flexWrap: "wrap" }}
  >
    <FormControl size="small" sx={{ minWidth: 160, flex: "0 0 160px" }}>
      <InputLabel>استان</InputLabel>
      <Select
        value={provinceFilter}
        label="استان"
        onChange={(e) => onProvinceChange(e.target.value)}
      >
        <MenuItem value="">همه</MenuItem>
        {provinces.map((p) => {
          const id = optionId(p);
          return (
            <MenuItem key={String(id)} value={id}>
              {optionLabel(p, ["provinceName", "ProvinceName", "label"])}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>

    <FormControl
      size="small"
      sx={{ minWidth: 160, flex: "0 0 160px" }}
      disabled={!provinceFilter}
    >
      <InputLabel>شهر</InputLabel>
      <Select
        value={cityFilter}
        label="شهر"
        onChange={(e) => onCityChange(e.target.value)}
      >
        <MenuItem value="">همه</MenuItem>
        {filteredCities.map((c) => {
          const id = optionId(c);
          return (
            <MenuItem key={String(id)} value={id}>
              {optionLabel(c, ["cityName", "CityName", "label"])}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  </Stack>
);

SupervisorLocationFilterBar.propTypes = {
  provinceFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cityFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  provinces: PropTypes.array,
  filteredCities: PropTypes.array,
  onProvinceChange: PropTypes.func.isRequired,
  onCityChange: PropTypes.func.isRequired,
};

SupervisorLocationFilterBar.defaultProps = {
  provinceFilter: "",
  cityFilter: "",
  provinces: [],
  filteredCities: [],
};

export default useSupervisorLocationFilters;
